<div class="report-viewer-container" *ngIf="reportData">
  <!-- Header section with navigation -->
  <div class="report-viewer-header flex justify-between items-center py-3 px-5">
    <div class="back-button-container flex items-center">
      <button class="btn-blue" (click)="goBackToReports()">
        <mat-icon>arrow_back</mat-icon>
        <span>Back to Reports</span>
      </button>
    </div>

    <!-- AUE View Mode Toggle (show only for Model/AUE report) -->
    <div *ngIf="isModelAueReport" class="toggle-container flex items-center gap-3">
      <span [ngClass]="!aueViewModeControl.value ? 'font-semibold' : 'text-gray-500'">Relative Time</span>
      <lib-cdw-slide-toggle
        [formControl]="aueViewModeControl"
        ariaLabel="Toggle between Relative Time and Fixed Time views"
        [showCheckmark]="true"
        [trackColor]="toggleTrackBg"
        [thumbColor]="tertiaryBlueCta"
        [thumbUncheckedColor]="primaryWhite"
        [checkmarkColor]="primaryWhite"
        [minusIconColor]="primaryBlack"
      ></lib-cdw-slide-toggle>
      <span [ngClass]="aueViewModeControl.value ? 'font-semibold' : 'text-gray-500'">Fixed Time</span>
    </div>
  </div>

  <!-- User Report View -->
  <div *ngIf="isUserReport && userData" class="report-content">

    <div class="pl-10 pt-10 pb-4 w-[450px]">
      <lib-cdw-divider thickness="2px"></lib-cdw-divider>
      <h3 class="report-title mb-6">User Details</h3>
    </div>

    <div class="user-details">
      <!-- Header Row -->
      <div class="detail-header">Given Name</div>
      <div class="detail-header">Family Name</div>
      <div class="detail-header">User Email</div>
      <div class="detail-header">Last Login</div>
      <div class="detail-header">User OU</div>

      <!-- Value Row -->
      <div class="detail-value">{{ givenName }}</div>
      <div class="detail-value">{{ familyName }}</div>
      <div class="detail-value">{{ userData.email }}</div>
      <div class="detail-value">Coming Soon...</div>
      <div class="detail-value">Coming Soon...</div>
    </div>

    <!-- Device Sessions Grid -->
    <div class="w-full h-[550px] mt-8 relative user-report-grid">
      <!-- Loading Spinner -->
      <div *ngIf="isLoadingSessions" class="absolute inset-0 flex items-center justify-center bg-white z-[1000]">
        <div class="flex flex-col items-center gap-4">
          <mat-progress-spinner
            diameter="60"
            mode="indeterminate"
            color="primary">
          </mat-progress-spinner>
          <p class="text-gray-600 font-medium">Loading device sessions...</p>
        </div>
      </div>

      <!-- Grid -->
      <ag-grid-angular
        class="ag-theme-alpine w-full h-full"
        [rowData]="deviceSessions"
        [columnDefs]="columnDefs"
        [gridOptions]="gridOptions"
      >
      </ag-grid-angular>
    </div>
  </div>

  <!-- Aggregate Report View -->
  <div *ngIf="isAggregateReport || isLoadingReport" class="report-content">
    <!-- Filter Bar Row with Header -->
    <div class="pl-5 pt-6">
      <div class="flex items-center">
        <div class="flex-1">
          <cdw-ae-filter-bar-core
            [filterBarField]="aggregateFilterBarSettings"
            [initialSelectedFilterFields$]="aggregateInitialFilters$"
            [dialogComponent]="filterBarDialogComponent"
            (filtersChange)="onAggregateFiltersChange($event)"
          ></cdw-ae-filter-bar-core>
        </div>
        <!-- Header Text -->
        <div *ngIf="gaugeSummaryConfig?.headerText" class="flex-1 text-center total-devices-text">
          <span>{{ gaugeSummaryConfig!.headerText }}</span>
        </div>
        <!-- Header Icon -->
        <div *ngIf="gaugeSummaryConfig?.headerIcon" class="flex-1 text-center">
          <mat-icon class="gauge-header-icon">{{ gaugeSummaryConfig!.headerIcon }}</mat-icon>
        </div>
        <div class="flex-1"></div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div *ngIf="isLoadingReport" class="relative min-h-[600px] flex items-center justify-center bg-white">
      <div class="flex flex-col items-center gap-4">
        <mat-progress-spinner
          diameter="60"
          mode="indeterminate"
          color="primary">
        </mat-progress-spinner>
        <p class="text-gray-600 font-medium">Loading {{ reportTitle }}...</p>
      </div>
    </div>

    <!-- Report Content (shown when not loading) -->
    <div *ngIf="!isLoadingReport">

      <!-- Gauge Summary Cards -->
      <div *ngIf="gaugeSummaryConfig" class="px-5 mt-6">
        <div class="grid gap-6" style="grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));">
          <div *ngFor="let card of gaugeSummaryConfig.cards" class="flex flex-col border border-gray-300 rounded-lg bg-white shadow-md">
            <div class="px-4 pt-1">
              <h4 class="gauge-card-title pb-2">{{ card.title }}</h4>
            </div>
            <div class="border-b-2" [style.border-bottom-color]="card.borderColor"></div>
            <div class="flex flex-row items-center p-4">
              <div class="flex-1" style="height: 120px; min-width: 0;">
                <spog-gauge-chart
                  [dataSource]="card.percentage"
                  [customColors]="card.gaugeColor"
                  [showXAxis]="false"
                  [showYAxis]="true"
                  [showLegend]="false"
                  [animations]="true"
                  [tooltipDisabled]="false"
                  style="width: 100%; height: 100%;"
                ></spog-gauge-chart>
              </div>
              <div class="flex flex-col items-center -ml-6">
                <!-- Show value if showValue is not false (defaults to true) -->
                <div *ngIf="card.showValue !== false" class="gauge-card-device-count">{{ card.value }}</div>
                <!-- Show custom image if provided, otherwise Material icon, otherwise text label -->
                <img *ngIf="card.iconImage" [src]="card.iconImage" class="gauge-card-icon-image" alt="" style="width: 80px !important; height: 80px !important; min-width: 80px; min-height: 80px;">
                <mat-icon *ngIf="!card.iconImage && card.icon" class="gauge-card-icon">{{ card.icon }}</mat-icon>
                <div *ngIf="!card.iconImage && !card.icon" class="gauge-card-device-label">{{ card.label || 'Devices' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Aggregate Report Grid -->
      <div class="w-full h-[550px] mt-8 aggregate-report-grid">
        <ag-grid-angular
          class="ag-theme-alpine w-full h-full"
          [rowData]="filteredAggregateData"
          [columnDefs]="columnDefs"
          [gridOptions]="gridOptions"
        >
        </ag-grid-angular>
      </div>
    </div>
  </div>

  <!-- Placeholder for other report types -->
  <div *ngIf="!isUserReport && !isAggregateReport" class="report-content padded-section">
    <div class="report-header">
      <h3 class="report-title">Report Not Implemented</h3>
    </div>
    <p class="text-gray-600">This report type ({{ reportData.type }}) is not yet implemented.</p>
  </div>
</div>

<div class="report-viewer-empty" *ngIf="!reportData">
  <p class="text-gray-500">No report data available. Please perform a search to view a report.</p>
</div>
